import videojs from 'video.js';
import utils from './utils.js';

const DEFAULTS = {

};

/**
 * The class for changing boundaries of moments in video with gaps
 *
 * @param {Object} player   Main Player
 * @param {Object=} options Object of option names and values
 * @extends EventTarget
 */
class MomentsGaps extends videojs.EventTarget {
  constructor(player, options) {

    let options_ = Object.assign({}, DEFAULTS, options);

    super(player, options_);
    this.options = options_;
    this.gaps = this._createGapList(this.options.gaps);
  }

  _createGapList(gaps) {
    if (!gaps || !gaps.length) {
      return [];
    }

    return gaps
      .map((gap) => {
        let obj = {};
        obj.start = gap.start * 1;
        obj.finish = gap.finish * 1;
        obj.duration = obj.finish - obj.start;
        return obj;
      });
  }

  updateGapList(gaps) {
    this.gaps = this._createGapList(gaps);
  }

  /**
   * Function change time boundaries of the moments given the available gaps in video
   * @param {Array} moments - Array of moments which will be shown on timeline
   */
  changeMomentsBoundaries(moments) {
    // If no data about gaps in the video
    if (!this.gaps || !this.gaps.length) {
      // return moments without any changes
      return moments;
    }

    // Делаем копию всех объектов, с помощью spread-operator
    let momentsCopy = moments.map((moment) => {
      return {...moment};
    });

    momentsCopy = momentsCopy.map((moment) => {
      moment.start = moment.start * 1;
      moment.finish = moment.finish * 1;
      // Create property with data about the effect of gap at the moment
      moment.gapData = {
        startOffset: 0,                        // Offset of the begin of moment
        finishOffset: 0,                       // Offset of the finish of moment
        updatedStart: moment.start,            // New value of the begin of moment
        updatedFinish: moment.finish,          // New value of the finish of moment
        beginningGapLength: 0                  // Offset of the finish of moment when gap in the beginning of moment
      };
      return moment;
    });

    // Remove all moments which fully inside the gaps
    momentsCopy = this._filterMomentsInsideGaps(momentsCopy);
    // Change moments boundaries for every possible case
    momentsCopy = this._changeMomentsWithGaps(momentsCopy);
    momentsCopy = this._changeMomentsAfterGaps(momentsCopy);
    momentsCopy = this._changeMomentsPartiallyInGaps(momentsCopy);
    // Apply changes
    momentsCopy = this._applyChanges(momentsCopy);
    return momentsCopy;
  }

  /**
   * Function removes moments which are fully in the gaps
   * @param {Array} moments - Array of moments which will be shown on timeline
   */
  _filterMomentsInsideGaps(moments) {
    // Copy moments
    let result = [...moments];
    // For each gap in the video
    this.gaps.forEach((gap) => {
      // Filter moment which are fully in this gap
      result = result.filter((moment) => {
        let momentStartInGap = gap.start <= moment.start;
        let momentFinishInGap = gap.finish >= moment.finish;
        let momentFullyInGap = momentStartInGap && momentFinishInGap;
        // For ignoring moment fully inside the gap we should return false
        return !momentFullyInGap;
      });
    });
    return result;
  }

  /**
   * Function changes offsets of the boundaries which located after gaps
   * @param {Array} moments - Array of moments which will be shown on timeline
   */
  _changeMomentsAfterGaps(moments) {
    // Copy moments
    let result = [...moments];
    // For each moment in the video
    result = result.map((moment) => {
      // Check all gaps in the video
      this.gaps.forEach((gap) => {
        // If moment located after the gap
        let momentAfterGap = gap.finish < moment.start;
        if (momentAfterGap) {
          // Change moment's boundary
          moment.gapData.startOffset += gap.duration;
          moment.gapData.finishOffset += gap.duration;
        }
      });
      return moment;
    });
    return result;
  }

  /**
   * Function changes boundaries of moments which partially located in gaps
   * @param {Array} moments - Array of moments which will be shown on timeline
   */
  _changeMomentsPartiallyInGaps(moments) {
    // Copy moments
    let result = [...moments];
    // For each moment in the video
    result = result.map((moment) => {
      // Check each gap in the video
      this.gaps.forEach((gap) => {
        // Gap cut the beginning of the moment
        let gapCutMomentStart = gap.start <= moment.start && gap.finish >= moment.start ;
        // Gap cut the finishing of the moment
        let gapCutMomentFinish = gap.start <= moment.finish && gap.finish >= moment.finish;
        // If gap cut the beginning of moment
        if (gapCutMomentStart) {
          // If updated value of the beginning of moment before the gap
          if (moment.gapData.updatedStart > gap.start) {
            // Value of the beginning of moment need to be changed
            moment.gapData.updatedStart = gap.start;
            // Also value of the finishing of moment need to be changed
            // Calculate how much time gap cut from the beginning
            let beginningGapLength = gap.finish - moment.start;
            // For the case when moment has more than 1 gap in the beginnig
            // Value of the longest cut
            if (moment.gapData.beginningGapLength < beginningGapLength) {
              // Should be saved
              moment.gapData.beginningGapLength = beginningGapLength;
            }
          }
        }
        // If gap cut the finishing of moment
        if (gapCutMomentFinish) {
          // If updated value of the finishing of moment after the gap
          if (moment.gapData.updatedFinish > gap.start) {
            // Value of the finishing of moment need to be changed
            moment.gapData.updatedFinish = gap.start;
          }
        }
      });
      return moment;
    });
    return result;
  }

  /**
   * Function calculates offset value of the finish for moment which have gaps inside
   * @param {Array} moments - Array of moments which will be shown on timeline
   */
  _changeMomentsWithGaps(moments) {
    // Cope moments
    let result = [...moments];
    // For each moment
    result = result.map((moment) => {
      // Check every gap in the video
      this.gaps.forEach((gap) => {
        // Gap starts inside the moment
        let gapStartInMoment = gap.start >= moment.start;
        // Gap finishes inside the moment
        let gapFinishInMoment = gap.finish <= moment.finish;
        // If gap inside the moment
        if (gapStartInMoment && gapFinishInMoment) {
          // Change moment's finish offset
          moment.gapData.finishOffset += gap.duration;
        }
      });
      return moment;
    });
    return result;
  }

  /**
   * Change moments boundaries given the available gaps in video
   * @param {Array} moments - Array of moments which will be shown on timeline
   */
  _applyChanges(moments) {
    let result = [...moments];
    result = result.map((moment) => {
      // Firstly set new start and finish values
      moment.start = moment.gapData.updatedStart;
      moment.finish = moment.gapData.updatedFinish;
      // Then gap moment boundaries
      moment.start -= moment.gapData.startOffset;
      moment.finish -= moment.gapData.finishOffset + moment.gapData.beginningGapLength;
      return moment;
    });
    return result;
  }
}

export default MomentsGaps;
